import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useProtectedFileUrl from "../../hooks/useProtectedFileUrl";
import FilePreviewDialog from "../shared/FilePreviewDialog";
import { showToast } from "../../utils/toast";
import RequiredMark, { FieldLabel } from "../common/RequiredMark";
import "./CaregiverOnboarding.css";

function acceptsImageCapture(accept) {
  const normalized = String(accept || "").toLowerCase();
  if (!normalized) return true;
  return (
    normalized.includes("image") ||
    normalized.includes("jpg") ||
    normalized.includes("jpeg") ||
    normalized.includes("png") ||
    normalized.includes("webp") ||
    normalized.includes("heic")
  );
}

function isImageFile(fileOrName) {
  if (!fileOrName) return false;
  if (fileOrName instanceof File) {
    return (
      String(fileOrName.type || "").startsWith("image/") ||
      /\.(jpe?g|png|webp|gif|heic)$/i.test(fileOrName.name || "")
    );
  }
  const name =
    typeof fileOrName === "string" ? fileOrName : String(fileOrName.name || fileOrName || "");
  return /\.(jpe?g|png|webp|gif|heic)$/i.test(name);
}

function isPdfFile(fileOrName) {
  if (!fileOrName) return false;
  if (fileOrName instanceof File) {
    return fileOrName.type === "application/pdf" || /\.pdf$/i.test(fileOrName.name || "");
  }
  const name = typeof fileOrName === "string" ? fileOrName : String(fileOrName.name || "");
  return /\.pdf$/i.test(name);
}

function resolveStorageKey(file, existingStorageKey) {
  return String(file?.storageKey || file?.key || existingStorageKey || "").trim();
}

function resolveServerUrl(file, existingFileUrl) {
  return String(file?.url || existingFileUrl || "").trim();
}

function fileDisplayName(file, fileName) {
  if (file instanceof File) return file.name;
  if (file?.name) return file.name;
  return fileName || "";
}

/**
 * Shared upload control (caregiver onboarding). Supports preview, file picker, and camera capture.
 */
export default function CaregiverUploadField({
  inputId,
  title,
  fileName,
  file = null,
  previewUrl: previewUrlProp = "",
  /** Fallback when persisted file metadata lives on onboarding / profile form */
  existingFileUrl = "",
  existingStorageKey = "",
  /** True when a document is already stored on the server (show preview even before re-selecting a file) */
  alreadyUploaded = false,
  accept,
  onChange,
  helperText,
  required = false,
  disabled = false,
  compact = false,
  enableCamera,
  /** "environment" = rear camera (documents); "user" = front camera (selfie) */
  cameraFacing = "environment",
}) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);

  const showCamera = useMemo(() => {
    if (enableCamera === false) return false;
    if (enableCamera === true) return true;
    return acceptsImageCapture(accept);
  }, [accept, enableCamera]);

  const cameraInputId = `${inputId}-camera`;
  const displayName = fileDisplayName(file, fileName);
  const hasSelection = Boolean(displayName) || Boolean(alreadyUploaded);

  const isNativeFile = file instanceof File;
  const serverUrl = isNativeFile ? "" : resolveServerUrl(file, existingFileUrl);
  const serverKey = isNativeFile ? "" : resolveStorageKey(file, existingStorageKey);
  const protectedServerUrl = useProtectedFileUrl(
    isNativeFile ? "" : serverUrl,
    isNativeFile ? "" : serverKey
  );
  const nameForTypeCheck = displayName || fileName || "";

  useEffect(() => {
    if (isNativeFile && isImageFile(file)) {
      const url = URL.createObjectURL(file);
      setLocalPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setLocalPreviewUrl("");
    return undefined;
  }, [file, isNativeFile]);

  const previewSrc = useMemo(() => {
    if (localPreviewUrl) return localPreviewUrl;
    if (isNativeFile) return "";
    if (previewUrlProp) return previewUrlProp;
    if (protectedServerUrl) return protectedServerUrl;
    if (serverUrl) return serverUrl;
    return "";
  }, [localPreviewUrl, isNativeFile, previewUrlProp, protectedServerUrl, serverUrl]);

  const wantsImagePreview = isImageFile(file) || isImageFile(nameForTypeCheck);
  const wantsPdfPreview = isPdfFile(file) || isPdfFile(nameForTypeCheck);
  const previewLoading =
    hasSelection &&
    !previewSrc &&
    Boolean(serverUrl || serverKey) &&
    !isNativeFile &&
    (wantsImagePreview || wantsPdfPreview);

  const showImagePreview = hasSelection && wantsImagePreview && Boolean(previewSrc);
  const showPdfPreview = hasSelection && wantsPdfPreview && !wantsImagePreview;
  const showPreviewPanel = showImagePreview || showPdfPreview || previewLoading;

  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const closeCamera = useCallback(() => {
    stopCameraStream();
    setCameraOpen(false);
    setCameraBusy(false);
  }, [stopCameraStream]);

  useEffect(() => {
    if (!cameraOpen) return undefined;

    let cancelled = false;

    async function startCamera() {
      if (!navigator.mediaDevices?.getUserMedia) {
        closeCamera();
        cameraInputRef.current?.click();
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: cameraFacing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        showToast.error(
          err?.name === "NotAllowedError"
            ? "Camera permission is required to take a photo."
            : "Could not open the camera. Try Choose file instead."
        );
        closeCamera();
        cameraInputRef.current?.click();
      }
    }

    startCamera();

    return () => {
      cancelled = true;
      stopCameraStream();
    };
  }, [cameraOpen, cameraFacing, closeCamera, stopCameraStream]);

  useEffect(() => () => stopCameraStream(), [stopCameraStream]);

  function clearInputValues() {
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  }

  function handleSelectedFile(nextFile) {
    onChange(nextFile || null);
    clearInputValues();
  }

  function openCamera() {
    if (disabled) return;
    setCameraOpen(true);
  }

  async function capturePhoto() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      showToast.error("Camera is not ready yet. Wait a moment and try again.");
      return;
    }
    setCameraBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Capture failed"))),
          "image/jpeg",
          0.92
        );
      });
      const captured = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
      handleSelectedFile(captured);
      closeCamera();
    } catch {
      showToast.error("Could not capture the photo. Please try again.");
    } finally {
      setCameraBusy(false);
    }
  }

  const statusBadge = (
    <span className={`cg-upload-field__status ${hasSelection ? "is-selected" : ""}`}>
      {hasSelection ? "Selected" : "No file"}
    </span>
  );

  const onFileNote =
    alreadyUploaded && !file
      ? "Saved on file. Use Replace file to upload a new image."
      : file?.persisted
        ? "On file — use Replace file to update."
        : null;

  const showCompactThumb =
    compact && (showPreviewPanel || previewLoading || (wantsImagePreview && hasSelection));

  function renderPreviewContent({ thumb = false }) {
    if (previewLoading) {
      return (
        <p className="cg-upload-field__preview-loading" aria-live="polite">
          Loading…
        </p>
      );
    }
    if (showImagePreview) {
      return (
        <button
          type="button"
          className="cg-upload-field__preview-link"
          onClick={() => setFilePreviewOpen(true)}
          title="Open full preview"
        >
          <img src={previewSrc} alt={`Preview of ${displayName || title}`} />
        </button>
      );
    }
    if (thumb && wantsImagePreview && hasSelection) {
      return (
        <div className="cg-upload-field__thumb-placeholder" aria-hidden="true">
          <span>IMG</span>
        </div>
      );
    }
    return (
      <div className="cg-upload-field__preview-pdf" aria-label="Document on file">
        <span className="cg-upload-field__preview-pdf-icon">PDF</span>
        <span className="cg-upload-field__preview-pdf-name">{displayName || fileName || "Document"}</span>
        {previewSrc ? (
          <button type="button" className="cg-upload-field__preview-open" onClick={() => setFilePreviewOpen(true)}>
            View document
          </button>
        ) : null}
      </div>
    );
  }

  const fileInputs = (
    <>
      <input
        ref={fileInputRef}
        id={inputId}
        className="cg-upload-field__input"
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={(e) => handleSelectedFile(e.target.files?.[0] || null)}
      />
      {showCamera ? (
        <input
          ref={cameraInputRef}
          id={cameraInputId}
          className="cg-upload-field__input"
          type="file"
          accept="image/*"
          capture={cameraFacing}
          disabled={disabled}
          onChange={(e) => handleSelectedFile(e.target.files?.[0] || null)}
        />
      ) : null}
    </>
  );

  const actionsBlock = (
    <div className="cg-upload-field__actions">
      <label htmlFor={inputId} className="cg-upload-field__button">
        {hasSelection ? "Replace file" : "Choose file"}
      </label>
      {showCamera ? (
        <button
          type="button"
          className="cg-upload-field__button cg-upload-field__button--camera"
          disabled={disabled}
          onClick={openCamera}
        >
          Take photo
        </button>
      ) : null}
    </div>
  );

  const metaBlock = (statusInMeta = false) => (
    <div className="cg-upload-field__meta">
      <div className="cg-upload-field__meta-row">
        <span className="cg-upload-field__name">{displayName || "No file selected yet"}</span>
        {statusInMeta ? statusBadge : null}
      </div>
      {onFileNote && statusInMeta ? <p className="cg-upload-field__on-file">{onFileNote}</p> : null}
      {file && !file.persisted ? (
        <button
          type="button"
          className="cg-upload-field__clear"
          disabled={disabled}
          onClick={() => handleSelectedFile(null)}
        >
          Remove selection
        </button>
      ) : null}
      {helperText ? <small className="cg-upload-field__helper">{helperText}</small> : null}
      {showCamera && !compact ? (
        <small className="cg-upload-field__camera-hint">
          Take photo opens your device camera (allow permission when prompted). Preview appears above
          after you select or capture an image.
        </small>
      ) : null}
      {showCamera && compact ? (
        <small className="cg-upload-field__camera-hint">Take photo uses your device camera when available.</small>
      ) : null}
    </div>
  );

  const previewBlock = (thumb = false) =>
    showPreviewPanel || (thumb && previewLoading) || (thumb && wantsImagePreview && hasSelection) ? (
      <div className={`cg-upload-field__preview ${thumb ? "cg-upload-field__preview--thumb" : ""}`}>
        {renderPreviewContent({ thumb })}
        {!thumb && file && !file.persisted ? (
          <button
            type="button"
            className="cg-upload-field__clear"
            disabled={disabled}
            onClick={() => handleSelectedFile(null)}
          >
            Remove
          </button>
        ) : null}
        {!thumb && onFileNote ? <span className="cg-upload-field__on-file">{onFileNote}</span> : null}
      </div>
    ) : null;

  return (
    <>
      <div className={`cg-upload-field ${compact ? "cg-upload-field--compact" : ""}${disabled ? " is-disabled" : ""}`}>
        <div className="cg-upload-field__head">
          <FieldLabel required={required} className="field-label--upload field-label--strong">
            {title}
          </FieldLabel>
          {!compact ? statusBadge : null}
        </div>

        {fileInputs}

        {compact ? (
          <div className="cg-upload-field__compact-layout">
            {showCompactThumb ? (
              <div className="cg-upload-field__thumb">{previewBlock(true)}</div>
            ) : null}
            <div className="cg-upload-field__compact-details">
              {actionsBlock}
              {metaBlock(true)}
            </div>
          </div>
        ) : (
          <>
            {previewBlock(false)}
            <div className="cg-upload-field__body">
              {actionsBlock}
              {metaBlock(false)}
            </div>
          </>
        )}
      </div>

      <FilePreviewDialog
        open={filePreviewOpen && Boolean(previewSrc)}
        onClose={() => setFilePreviewOpen(false)}
        title={title}
        fileName={displayName || fileName || ""}
        src={previewSrc}
        contentType={file instanceof File ? file.type : ""}
      />

      {cameraOpen ? (
        <div className="cg-camera-modal" role="dialog" aria-modal="true" aria-label={`Camera for ${title}`}>
          <div className="cg-camera-modal__panel">
            <header className="cg-camera-modal__head">
              <h3>{title}</h3>
              <button type="button" className="cg-camera-modal__close" onClick={closeCamera} aria-label="Close">
                ×
              </button>
            </header>
            <div className="cg-camera-modal__viewport">
              <video ref={videoRef} playsInline muted autoPlay className="cg-camera-modal__video" />
            </div>
            <p className="cg-camera-modal__hint">
              {cameraFacing === "user"
                ? "Position your face in the frame, then tap Capture."
                : "Align the document in the frame, then tap Capture."}
            </p>
            <div className="cg-camera-modal__actions">
              <button type="button" className="cg-upload-field__button" onClick={closeCamera} disabled={cameraBusy}>
                Cancel
              </button>
              <button
                type="button"
                className="cg-upload-field__button cg-upload-field__button--camera"
                onClick={capturePhoto}
                disabled={cameraBusy}
              >
                {cameraBusy ? "Saving…" : "Capture photo"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
